import React from 'react';
import styled from 'styled-components';
import { Image } from 'react-datocms';
import { Button, CloseIcon } from '@kvdbil/components';
import { FileField, ResponsiveImage } from '~/config/generated/graphql';
import { useTranslation } from '~/Locale';
import Lightbox from '~/App/shared/components/Lightbox';
import { Close } from '~/App/shared/components/IconButtons/Close';
import AiGeneratedImageWrapper from '~/App/components/DatoCMS/AiGeneratedImageWrapper';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
`;

const ImageArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
  container-type: size;
`;

const ImageBox = styled(AiGeneratedImageWrapper)<{ $aspectRatio: number }>`
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  width: ${({ $aspectRatio }) => `min(100%, calc(100cqh * ${$aspectRatio}))`};
  max-width: 100%;
  max-height: 100%;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem;
`;

const contentStyles = {
  width: '100vw',
  maxWidth: '100vw',
  height: '100vh',
  maxHeight: '100vh',
  margin: 0,
  padding: 0,
  borderRadius: 0,
  overflow: 'hidden'
};

export type LightboxImage = {
  responsiveImage: ResponsiveImage;
  tags?: FileField['tags'];
};

type Props = {
  isOpen: boolean;
  image: LightboxImage | null;
  onClose: () => void;
};

const getAspectRatio = ({ aspectRatio, width, height }: ResponsiveImage) =>
  Number(aspectRatio) || Number(width) / Number(height) || 1;

const ImageLightbox = ({ isOpen, image, onClose }: Props) => {
  const { t } = useTranslation();

  return (
    <Lightbox isOpen={isOpen} onClose={onClose} contentStyles={contentStyles}>
      {image && (
        <Layout>
          <Header>
            <Close onClick={onClose} />
          </Header>
          <ImageArea>
            <ImageBox
              tags={image.tags}
              $aspectRatio={getAspectRatio(image.responsiveImage)}
            >
              <Image
                data={image.responsiveImage}
                style={{ width: '100%', height: '100%', maxWidth: 'none' }}
                objectFit="contain"
              />
            </ImageBox>
          </ImageArea>
          <Footer>
            <Button
              onClick={onClose}
              color="secondary"
              sufixIcon={<CloseIcon />}
            >
              {t('Close')}
            </Button>
          </Footer>
        </Layout>
      )}
    </Lightbox>
  );
};

export default ImageLightbox;
